import React, { useEffect, useRef, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { ChevronDownIcon } from 'src/icons/ChevronDownIcon';
import { List, ListItem, ListItemIcon, ListItemText, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { theme } from "src/theme/mui-theme";

export interface MainAndSubItemIndex {
    mainIndex: number;
    subIndex?: number;
}

interface NestedAccordionViewProps<TMain, TSub, TItem> {
    data: TMain[];

    getMainLabel: (main: TMain) => string;
    getSubList: (main: TMain) => TSub[];
    getSubLabel: (sub: TSub) => string;
    getItems: (sub: TSub) => TItem[];
    getItemKey: (item: TItem) => string | number;
    getItemLabel: (item: TItem) => string;

    renderMainIcon?: (main: TMain) => React.ReactNode;
    renderMainActions?: (main: TMain) => React.ReactNode;
    getMainDomId?: (main: TMain) => string;

    renderSubIcon?: (sub: TSub, isExpanded: boolean) => React.ReactNode;
    renderSubActions?: (sub: TSub, isExpanded: boolean) => React.ReactNode;

    renderItemIcon?: (item: TItem) => string;

    /**
     * Fully custom item rendering. When provided, replaces the default ListItem.
     * The provided `ref` should be forwarded to the rendered element so the
     * parent can scroll it into view. The `selected` flag is derived from
     * `selectedItemKey` and should be forwarded for highlight styling.
     */
    renderItem?: (item: TItem, ref: React.Ref<HTMLLIElement>, selected: boolean) => React.ReactNode;

    onItemClick?: (item: TItem) => void;
    selectedItemKey?: string | number | null;

    mainLabelColor?: string;
    mainExpandIconColor?: string;
    onMainItemExpansionStateChange?: (main: TMain, isExpanded: boolean) => void;
    onSubExpansionStateChange?: (main: TMain, sub: TSub, isExpanded: boolean) => void;
    expandedMainAndSubItem?: MainAndSubItemIndex | null;
    expansionTrigger?: unknown;
    singleMainExpanded?: boolean;
    autoExpandAll?: boolean;
}

export function NestedAccordionView<TMain, TSub, TItem>({
    data,
    getMainLabel,
    getSubList,
    getSubLabel,
    getItems,
    getItemKey,
    getItemLabel,

    renderMainIcon,
    renderMainActions,
    getMainDomId,

    renderSubIcon,
    renderSubActions,

    renderItemIcon,
    renderItem,

    onItemClick,
    selectedItemKey,

    mainLabelColor,
    mainExpandIconColor,
    onMainItemExpansionStateChange,
    onSubExpansionStateChange,
    expandedMainAndSubItem,
    expansionTrigger,
    singleMainExpanded,
    autoExpandAll,
}: NestedAccordionViewProps<TMain, TSub, TItem>) {

    const [mainExpanded, setMainExpanded] = useState<Record<number, boolean>>({});
    const [subExpanded, setSubExpanded] = useState<Record<string, boolean>>({});
    const subRefs = useRef<Record<string, HTMLElement | null>>({});
    const itemRefs = useRef<Record<string, HTMLElement | null>>({});

    useEffect(() => {
        if (autoExpandAll) return;
        if (expandedMainAndSubItem == null) {
            setMainExpanded({});
            setSubExpanded({});
            return;
        }
        const { mainIndex, subIndex } = expandedMainAndSubItem;
        setMainExpanded({ [mainIndex]: true });
        setSubExpanded(subIndex != null ? { [`${mainIndex}-${subIndex}`]: true } : {});
        if (subIndex == null) return;
        const timeout = setTimeout(() => {
            subRefs.current[`${mainIndex}-${subIndex}`]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, theme.transitions.duration.standard);
        return () => clearTimeout(timeout);
    }, [expandedMainAndSubItem, expansionTrigger, autoExpandAll]);

    // While searching, expand every matching group by default but keep it
    // collapsible (this is plain state, not a hard override). Re-runs only when
    // the result set changes, so manual collapses survive until the next keystroke.
    useEffect(() => {
        if (!autoExpandAll) return;
        const nextMain: Record<number, boolean> = {};
        const nextSub: Record<string, boolean> = {};
        data.forEach((main, mainIndex) => {
            nextMain[mainIndex] = true;
            getSubList(main).forEach((_, subIndex) => {
                nextSub[`${mainIndex}-${subIndex}`] = true;
            });
        });
        setMainExpanded(nextMain);
        setSubExpanded(nextSub);
    }, [autoExpandAll, data, getSubList]);

    useEffect(() => {
        if (selectedItemKey == null) return;
        const timeout = setTimeout(() => {
            itemRefs.current[selectedItemKey.toString()]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, theme.transitions.duration.standard);
        return () => clearTimeout(timeout);
    }, [selectedItemKey]);

    return (
        <>
            {data.map((main, mainIndex) => {
                const isMainExpanded = Boolean(mainExpanded[mainIndex]);
                return (
                    <Accordion
                        key={mainIndex}
                        disableGutters
                        elevation={0}
                        square
                        sx={{
                            border: 'none',
                            boxShadow: 'none',
                            mb: 0,
                            '&::before': { display: 'none' },
                        }}
                        slotProps={{ transition: { unmountOnExit: true } }}
                        expanded={isMainExpanded}
                        onChange={(_, isExpanded: boolean) => {
                          if (singleMainExpanded) {
                            setMainExpanded(isExpanded ? { [mainIndex]: true } : {});
                          } else {
                            setMainExpanded((prev) => ({ ...prev, [mainIndex]: isExpanded }));
                          }
                          if (onMainItemExpansionStateChange != null) {
                            onMainItemExpansionStateChange(main, isExpanded)
                          }
                        }}
                    >
                        <AccordionSummary
                            id={getMainDomId?.(main)}
                            expandIcon={
                                <ChevronDownIcon
                                    sx={{
                                        color: mainExpandIconColor || theme.palette.text.primary,
                                        fontSize: 28,
                                    }}
                                />
                            }
                        >
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                width="100%"
                            >
                                <Stack direction="row" spacing={1} alignItems="center">
                                    {renderMainIcon?.(main)}
                                    <Typography variant="h6" color={mainLabelColor || theme.palette.text.primary}>
                                        {getMainLabel(main)}
                                    </Typography>
                                </Stack>

                                <Stack direction="row" spacing={1} alignItems="center" mr={2}>
                                    {renderMainActions?.(main)}
                                </Stack>
                            </Stack>
                        </AccordionSummary>

                        <AccordionDetails>
                            {getSubList(main).map((sub, subIndex) => {
                                const subKey = `${mainIndex}-${subIndex}`;
                                const isSubExpanded = Boolean(subExpanded[subKey]);
                                return (
                                    <Accordion
                                        ref={(el) => { subRefs.current[subKey] = el; }}
                                        key={subIndex}
                                        disableGutters
                                        elevation={0}
                                        square={false}
                                        sx={{
                                            borderRadius: 2,
                                            mb: 1,
                                            '&::before': { display: 'none' },
                                        }}
                                        slotProps={{ transition: { unmountOnExit: true } }}
                                        expanded={isSubExpanded}
                                        onChange={(_, isExpanded) => {
                                            setSubExpanded((prev) => ({
                                                ...prev,
                                                [subKey]: isExpanded,
                                            }));
                                            onSubExpansionStateChange?.(main, sub, isExpanded);
                                        }}
                                    >
                                        <AccordionSummary
                                            sx={{
                                                backgroundColor: isSubExpanded
                                                    ? `${theme.palette.primary.main}0D`
                                                    : theme.palette.grey[50],

                                                border: '1px solid',

                                                borderColor: isSubExpanded
                                                    ? `${theme.palette.primary.main}1A`
                                                    : theme.palette.grey[200],
                                            }}
                                            expandIcon={
                                                <ChevronDownIcon
                                                    sx={{
                                                        color: isSubExpanded
                                                            ? theme.palette.primary.main
                                                            : theme.palette.text.primary,
                                                    }}
                                                />
                                            }
                                        >
                                            <Stack
                                                direction="row"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                width="100%"
                                            >
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    {renderSubIcon?.(sub, isSubExpanded)}
                                                    <Typography
                                                        fontWeight="semibold"
                                                        sx={{
                                                            color: isSubExpanded
                                                                ? theme.palette.primary.main
                                                                : 'inherit',
                                                        }}
                                                    >
                                                        {getSubLabel(sub)}
                                                    </Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={1} alignItems="center" mr={2}>
                                                    {renderSubActions?.(sub, isSubExpanded)}
                                                </Stack>
                                            </Stack>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <List>
                                                {getItems(sub).map((item, index) => {
                                                    const itemKey = getItemKey(item);
                                                    const isSelected = selectedItemKey != null && itemKey === selectedItemKey;
                                                    const itemRef = (el: HTMLLIElement | null) => {
                                                        itemRefs.current[itemKey.toString()] = el;
                                                    };
                                                    return (
                                                    <React.Fragment key={itemKey + `${index}`}>
                                                        {renderItem ? renderItem(item, itemRef, isSelected) : (
                                                            <ListItem
                                                                ref={itemRef}
                                                                onClick={onItemClick ? () => onItemClick(item) : undefined}
                                                                sx={{
                                                                  cursor: onItemClick ? 'pointer' : 'default',
                                                                  backgroundColor: isSelected
                                                                      ? theme.palette.primary.light
                                                                      : 'transparent',
                                                                  ":hover": {
                                                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                                  }
                                                                }}
                                                            >
                                                                {renderItemIcon?.(item) && (
                                                                    <ListItemIcon>
                                                                        <img
                                                                            src={renderItemIcon(item)}
                                                                            alt={getItemLabel(item)}
                                                                            width={24}
                                                                            height={24}
                                                                        />
                                                                    </ListItemIcon>
                                                                )}
                                                                <ListItemText primary={getItemLabel(item)} />
                                                            </ListItem>
                                                        )}
                                                    </React.Fragment>
                                                    );
                                                })}
                                            </List>
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </>
    );
}